/**
 * MessageTemplateForm コンポーネント
 *
 * メッセージテンプレートの作成・編集フォームを提供するコンポーネント。
 * 初期値を受け取り、フォームの状態管理や送信処理を行います。
 *
 * @file メッセージテンプレートフォーム
 * @date 2025/06/26
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Form, Input, Radio, Select, Space, Upload, Image, message } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { MessageTemplateFormType } from 'src/models/messageTemplate';
import { useMessageTemplateForm } from 'src/usecases/messageTemplate/form';
import TextArea from 'antd/es/input/TextArea';
import { RcFile, UploadProps } from 'antd/es/upload';
import { useMessageTemplateList } from 'src/usecases/messageTemplate/reader';

interface Props {
  init?: MessageTemplateFormType;
}

export const MessageTemplateForm: React.FC<Props> = ({ init }) => {
  // フォーム送信ロジックのカスタムhook
  const _formState = useMessageTemplateForm();

  // Ant DesignのFormインスタンス
  const [form] = Form.useForm<MessageTemplateFormType>();

  // フォーム送信中のローディング状態
  const [loading, isLoading] = useState(false);

  // 画像アップロード用の状態
  const [inputImage, setInputImage] = useState<RcFile | undefined>();

  // カテゴリ入力モードの状態
  const [isCustomCategoryMode, setIsCustomCategoryMode] = useState(false);
  const [customCategoryValue, setCustomCategoryValue] = useState('');
  
  // Selectの値を独立して管理
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(init?.category || undefined);

  // フォームの値(DM/配信)を監視
  const usage = Form.useWatch('usage', form);

  // usage変更時のハンドラー
  const handleUsageChange = (e: any) => {
    const newUsage = e.target.value;

    // 選択前の情報をクリア（categoryは除外）
    form.resetFields(['title', 'type', 'text', 'tempLabel', 'altText', 'tempUri']);

    // 画像も初期化
    setInputImage(undefined);

    // initが設定されている場合（既存テンプレート編集時）はカテゴリを維持
    if (init?.category) {
      // カテゴリ関連の状態は初期値を維持
      setIsCustomCategoryMode(false);
      setCustomCategoryValue('');
      setSelectedCategory(init.category);
      form.setFieldValue('category', init.category);
    } else {
      // 新規作成時はカテゴリもリセット
      form.resetFields(['category']);
      setIsCustomCategoryMode(false);
      setCustomCategoryValue('');
      setSelectedCategory(undefined);
    }

    // 新しいusage値を設定
    form.setFieldValue('usage', newUsage);
  };

  // カテゴリ選択時のハンドラー
  const handleCategorySelectChange = (value: string) => {
    if (value === '__CUSTOM_INPUT__') {
      setIsCustomCategoryMode(true);
      setCustomCategoryValue('');
      setSelectedCategory(undefined);
      form.setFieldValue('category', ''); // undefinedではなく空文字を設定
    } else {
      setIsCustomCategoryMode(false);
      setCustomCategoryValue('');
      setSelectedCategory(value);
      form.setFieldValue('category', value);
    }
  };

  // カスタムカテゴリ入力変更時のハンドラー
  const handleCustomCategoryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomCategoryValue(value);
    setSelectedCategory(value);
    form.setFieldValue('category', value);
  };

  // カスタムカテゴリ入力をキャンセル
  const handleCancelNewCategory = () => {
    setIsCustomCategoryMode(false);
    setCustomCategoryValue('');
    setSelectedCategory(undefined);
    form.setFieldValue('category', ''); // undefinedではなく空文字を設定
  };

  // フォームの値(送信内容タイプ)を監視
  const type = Form.useWatch('type', form);

  /**
   * フォーム送信処理
   * ローディング状態を管理しながらAPIコールを実行
   */
  const confirm = useCallback(
    async (value: MessageTemplateFormType) => {
      isLoading(true);
      try {
        // undefinedやnullの値をチェックして適切な値に変換
        const sanitizedValue = {
          ...value,
          category: value.category || '', // undefinedの場合は空文字に変換
        };
        await _formState.submit({ ...sanitizedValue, messageTemplate: init?.messageTemplate }, inputImage);
      } finally {
        isLoading(false);
      }
    },
    [_formState, init, inputImage]
  );

  const values = useMessageTemplateList();

  // 使用済みカテゴリを取得
  const usedCategories = useMemo(() => {
    const categories = new Set<string>();
    values.data?.messageTemplates?.forEach(template => {
      if (template.category) {
        categories.add(template.category);
      }
    });
    return Array.from(categories);
  }, [values.data?.messageTemplates]);

  // カテゴリ選択肢を生成
  const categoryOptions: { value: string; label: string }[] = useMemo(() => {
    const options: { value: string; label: string }[] = [];
    
    // 1. initにカテゴリが設定されている場合は必ず最初に追加
    if (init?.category) {
      options.push({ value: init.category, label: init.category });
    }
    
    // 2. 取得済みの他のカテゴリを追加（initのカテゴリと重複しないように）
    usedCategories.forEach(category => {
      if (category !== init?.category) {
        options.push({ value: category, label: category });
      }
    });
    
    // 3. 最後に新規カテゴリ入力オプションを追加
    options.push({ value: '__CUSTOM_INPUT__', label: '+ カテゴリを入力' });
    
    return options;
  }, [usedCategories, init?.category]);

  useEffect(() => {
    // initにカテゴリが設定されている場合、即座にフォームと状態に設定
    if (init?.category && !form.getFieldValue('category')) {
      setIsCustomCategoryMode(false);
      setSelectedCategory(init.category);
      form.setFieldValue('category', init.category);
    }
  }, [init?.category, form]);

  // 送信内容タイプに基づく表示制御
  const _needText = type === 'text' || type === 'textAndImage' || type === 'textAndTemplate';
  const _needImage = type === 'image' || type === 'textAndImage';
  const _needTemplate = type === 'template' || type === 'textAndTemplate';

  // 画像アップロードの設定
  const imageProps: UploadProps = {
    accept: 'image/*',
    action: '/upload.do',
    maxCount: 1,
    showUploadList: inputImage !== undefined,
    beforeUpload: (file: RcFile) => {
      const isImage =
        file.type === 'image/png' ||
        file.type === 'image/jpeg' ||
        file.type === 'image/jpg';

      if (!isImage) {
        message.error(`png,jpeg,jpg形式のみアップロード可能です`);
        return Upload.LIST_IGNORE;
      }

      setInputImage(file);
      return false;
    },
    onRemove: () => setInputImage(undefined),
  };

  return (
    <Card style={{ paddingTop: '3%' }}>
      <Form
        initialValues={init}
        labelWrap
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 16, offset: 1 }}
        layout="horizontal"
        style={{ maxWidth: 900, margin: 'auto' }}
        form={form}
        onFinish={confirm}
        disabled={loading}
      >
        <Form.Item<MessageTemplateFormType>
          name="usage"
          label="DM/配信"
          rules={[{ required: true, message: '使用目的を選択してください' }]}
        >
          <Radio.Group onChange={handleUsageChange}>
            <Radio value="directMessage">DM</Radio>
            <Radio value="broadcast">配信</Radio>
          </Radio.Group>
        </Form.Item>

        {usage === 'directMessage' && (
          <>
            <Form.Item<MessageTemplateFormType>
              name="category"
              label="カテゴリ"
              rules={[
                {
                  required: true,
                  message: isCustomCategoryMode ? 'カテゴリ名を入力してください' : 'カテゴリを選択してください'
                }
              ]}
            >
              <div>
                <Select
                  placeholder="カテゴリを選択してください"
                  options={categoryOptions}
                  onChange={handleCategorySelectChange}
                  disabled={isCustomCategoryMode}
                  value={isCustomCategoryMode ? undefined : selectedCategory}
                  key={`dm-category-${usage}`} // usage変更時に強制再レンダリング
                />
                {isCustomCategoryMode && (
                  <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                    <Input
                      style={{ flex: 1 }}
                      placeholder="カテゴリ名を入力してください"
                      value={customCategoryValue}
                      onChange={handleCustomCategoryChange}
                      maxLength={20}
                      autoFocus
                    />
                    <Button onClick={handleCancelNewCategory}>
                      キャンセル
                    </Button>
                  </div>
                )}
              </div>
            </Form.Item>

            <Form.Item<MessageTemplateFormType>
              name="text"
              label="テキスト"
              rules={[{ required: true, message: 'テキストを入力してください' }]}
            >
              <TextArea minLength={1} maxLength={300} placeholder="メッセージを入力してください" />
            </Form.Item>
          </>
        )}

        {usage === 'broadcast' && (
          <>
            <Form.Item<MessageTemplateFormType>
              name="title"
              label="タイトル"
              rules={[{ required: true, message: 'タイトルを入力してください' }]}
            >
              <Input placeholder="タイトルを入力してください" maxLength={100} />
            </Form.Item>

            <Form.Item<MessageTemplateFormType>
              name="category"
              label="カテゴリ"
              rules={[
                {
                  required: true,
                  message: isCustomCategoryMode ? 'カテゴリ名を入力してください' : 'カテゴリを選択してください'
                }
              ]}
            >
              <div>
                <Select
                  placeholder="カテゴリを選択してください"
                  options={categoryOptions}
                  onChange={handleCategorySelectChange}
                  disabled={isCustomCategoryMode}
                  value={isCustomCategoryMode ? undefined : selectedCategory}
                  key={`broadcast-category-${usage}`} // usage変更時に強制再レンダリング
                />
                {isCustomCategoryMode && (
                  <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                    <Input
                      style={{ flex: 1 }}
                      placeholder="カテゴリ名を入力してください"
                      value={customCategoryValue}
                      onChange={handleCustomCategoryChange}
                      maxLength={20}
                      autoFocus
                    />
                    <Button onClick={handleCancelNewCategory}>
                      キャンセル
                    </Button>
                  </div>
                )}
              </div>

            </Form.Item>

            <Form.Item<MessageTemplateFormType>
              name="type"
              label="送信内容タイプ"
              rules={[{ required: true, message: '送信内容タイプを選択してください' }]}
            >
              <Select placeholder="送信内容タイプを選択してください"
                options={[
                  { value: 'text', label: 'テキストのみ' },
                  { value: 'image', label: '画像のみ' },
                  {
                    value: 'template',
                    label: 'アクション付き画像のみ（クーポンなど）',
                  },
                  { value: 'textAndImage', label: 'テキスト＆画像' },
                  {
                    value: 'textAndTemplate',
                    label: 'テキスト＆アクション付き画像',
                  },
                ]}/>
            </Form.Item>

            {_needText && (
              <Form.Item<MessageTemplateFormType>
                name="text"
                label="テキスト"
                rules={[{ required: true, message: 'テキストを入力してください' }]}
              >
                <TextArea minLength={1} maxLength={300} placeholder="メッセージを入力してください" />
              </Form.Item>
            )}

            {(_needImage || _needTemplate) && (
              <Form.Item
                name="image"
                label="画像"
                rules={[{
                  required: _needImage
                    ? !init?.messageTemplate?.broadCastMessage?.imageUrl
                    : !init?.messageTemplate?.broadCastMessage?.tempImageUrl,
                  message: '画像をアップロードしてください'
                }]}
              >
                <Space style={{ maxWidth: '100%', overflow: 'auto' }}>
                  <Upload.Dragger {...imageProps} style={{ maxWidth: '400px' }}>
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                      クリックまたはファイルをドラッグ
                    </p>
                    <p className="ant-upload-hint">
                      png,jpeg,jpg形式に対応しています
                    </p>
                  </Upload.Dragger>
                  {((_needImage && init?.messageTemplate?.broadCastMessage?.imageUrl) ||
                    (_needTemplate && init?.messageTemplate?.broadCastMessage?.tempImageUrl)) && (
                     <Image
                       src={_needImage
                         ? init.messageTemplate.broadCastMessage.imageUrl ?? undefined
                         : init.messageTemplate.broadCastMessage.tempImageUrl ?? undefined}
                       width="200px"
                     />
                   )}
                </Space>
              </Form.Item>
            )}

            {_needTemplate && (
              <>
                <Form.Item<MessageTemplateFormType>
                  name="tempLabel"
                  label="画像上のテキスト"
                  rules={[{ required: true, message: '画像上のテキストを入力してください' }]}
                >
                  <Input
                    placeholder="クーポンをみる"
                    minLength={1}
                    maxLength={12}
                  />
                </Form.Item>

                <Form.Item<MessageTemplateFormType>
                  name="altText"
                  label="通知に表示されるテキスト"
                  rules={[{ required: true, message: '通知テキストを入力してください' }]}
                >
                  <Input placeholder="通知に表示されるテキスト" maxLength={400} />
                </Form.Item>

                <Form.Item<MessageTemplateFormType>
                  name="tempUri"
                  label="リンク先"
                  rules={[
                    { required: true, message: 'リンク先を入力してください' },
                    { type: 'url', message: '正しいURLを入力してください' }
                  ]}
                >
                  <Input placeholder="https://example.com" maxLength={1000} />
                </Form.Item>
              </>
            )}
          </>
        )}

        <Form.Item wrapperCol={{ span: 12, offset: 7 }}>
          <Button type="primary" htmlType="submit" loading={loading}>
            確定
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};